import type { Occupation } from "@/common/types/occupation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertMessage } from "@/components/ui/AlertMessage";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormField } from "@/components/ui/form/FormField";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SignUpSchemaType } from "@/schemas/signUp.schema";
import { t } from "i18next";
import { LockIcon, Mail } from "lucide-react";
import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Trans } from "react-i18next";

// TODO: Use Tailwind’s extend feature to manage colors, font sizes...

interface Props {
  onSubmit: (data: SignUpSchemaType) => void;
  isSubmitting: boolean;
  form: UseFormReturn<SignUpSchemaType>;
  occupations: Occupation[];
  loadingOccupations: boolean;
}

export const SignUpForm = (props: Props): React.ReactNode => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  return (
    <Form {...props.form}>
      <form
        className="w-full flex flex-col"
        onSubmit={props.form.handleSubmit(props.onSubmit)}
      >
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <FormField
              control={props.form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="sm:w-1/2">
                  <FormLabel>{t("signUp.form.firstNameLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("signUp.form.firstNamePlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={props.form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="sm:w-1/2">
                  <FormLabel>{t("signUp.form.lastNameLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("signUp.form.lastNamePlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex">
            <FormField
              control={props.form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="w-full flex flex-col space-y-1">
                  <FormLabel>{t("signUp.form.emailLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      icon={<Mail />}
                      placeholder={t("signUp.form.emailPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={props.form.control}
            name="occupation"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel htmlFor="occupation">
                  {t("signUp.form.occupation")}
                </FormLabel>
                <Select
                  disabled={props.loadingOccupations}
                  value={field.value}
                  onValueChange={(value) => {
                    const selected = props.occupations.find(
                      (occ) => occ.occupation === value
                    );
                    if (selected) {
                      props.form.setValue("occupation", value);
                      props.form.setValue("occupationId", selected.id);
                    }
                  }}
                >
                  <SelectTrigger id="occupation">
                    <SelectValue
                      placeholder={t("signUp.form.occupationPlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {props.occupations.map((occ) => (
                      <SelectItem key={occ.id} value={occ.occupation}>
                        {occ.occupation}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage>
                  {props.form.formState.errors.occupation?.message}
                </FormMessage>
              </FormItem>
            )}
          />
          <div className="flex flex-col sm:flex-row gap-4">
            <FormField
              control={props.form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("signUp.form.passwordLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      icon={<LockIcon />}
                      placeholder={t("signUp.form.passwordLabel")}
                      {...field}
                      type="password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={props.form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("signUp.form.passwordConfirmtionLabel")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      icon={<LockIcon />}
                      placeholder={t("signUp.form.passwordLabel")}
                      {...field}
                      type="password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-4 !mt-8">
            <div className="flex">
              <FormField
                control={props.form.control}
                name="acceptTerms"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-start gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                          id="acceptTerms"
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="leading-none">
                        <FormLabel
                          className="leading-none"
                          htmlFor="acceptTerms"
                        >
                          {t("signUp.form.acceptTerms.first")}{" "}
                        </FormLabel>
                        <span
                          className="text-primary underline cursor-pointer"
                          onClick={() => {
                            setOpenDialog(true);
                          }}
                        >
                          {t("signUp.form.acceptTerms.last")}
                        </span>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {props.form.formState.errors.root?.message && (
              <AlertMessage
                description={props.form.formState.errors.root.message}
                title="Error"
                variant="destructive"
              />
            )}
            <Button
              className="w-full"
              disabled={props.isSubmitting}
              size={"xl"}
              type="submit"
            >
              {props.isSubmitting && <LoadingSpinner />}
              {t("signUp.form.signUpButton")}
            </Button>
          </div>
        </div>
      </form>
      <AlertDialog open={openDialog} onOpenChange={setOpenDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("signUp.notice.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              <Trans
                i18nKey="signUp.notice.fullText"
                components={{
                  b: <strong />,
                  br: <br />,
                  p: <p />,
                  a: (
                    <a
                      className="text-primary underline"
                      rel="noopener noreferrer"
                      target="_blank"
                    />
                  ),
                }}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-center">
            <AlertDialogFooter className="flex justify-center items-center">
              <AlertDialogCancel asChild>
                <Button
                  variant={"outline"}
                  onClick={() => {
                    setOpenDialog(false);
                    props.form.setValue("acceptTerms", false);
                  }}
                >
                  {t("signUp.notice.cancelButton")}
                </Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button
                  onClick={() => {
                    setOpenDialog(false);
                    props.form.setValue("acceptTerms", true);
                  }}
                >
                  {t("signUp.notice.continueButton")}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
};
